/**
 * User Edit Form Component
 *
 * Form for editing user information with validation and error handling.
 * Uses React Hook Form with Zod validation.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { User, UpdateUserRequest } from '@/types/user-management';

interface UserEditFormProps {
  user: User;
  onSubmit: (data: UpdateUserRequest) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  error: string | null;
}

/**
 * User Edit Form Schema
 * Zod validation schema for user edit form
 */
const userEditFormSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(255, 'First name must be less than 255 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(255, 'Last name must be less than 255 characters'),
  email: z.string().email('Invalid email format'),
  phone: z
    .string()
    .regex(/^\+1\d{10}$/, 'Phone must be a US E.164 number in the form +1XXXXXXXXXX'),
  dateOfBirth: z.string().optional(),
  pushNotificationsEnabled: z.boolean(),
});

type UserEditFormValues = z.infer<typeof userEditFormSchema>;

/**
 * User Edit Form
 * Main form component for editing user information
 */
export function UserEditForm({ user, onSubmit, onCancel, isSubmitting, error }: UserEditFormProps) {
  // Initialize form with user data
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<UserEditFormValues>({
    resolver: zodResolver(userEditFormSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      dateOfBirth: user.dateOfBirth || '',
      pushNotificationsEnabled: user.pushNotificationsEnabled ?? false,
    },
  });

  // Handle form submission. Only fields that actually changed from the loaded
  // record are submitted, so a phone-only edit sends just `{ phone }`. The API
  // re-derives the diff independently and does not rely on this filtering.
  const handleSubmit = async (data: UserEditFormValues) => {
    const updateData: UpdateUserRequest = {};

    if (data.firstName !== user.firstName) updateData.firstName = data.firstName;
    if (data.lastName !== user.lastName) updateData.lastName = data.lastName;
    // Email is immutable server-side; only forward a change so the API can
    // surface its explicit rejection rather than silently dropping it.
    if (data.email !== user.email) updateData.email = data.email;
    if (data.phone !== user.phone) updateData.phone = data.phone;
    if ((data.pushNotificationsEnabled ?? false) !== (user.pushNotificationsEnabled ?? false)) {
      updateData.pushNotificationsEnabled = data.pushNotificationsEnabled;
    }
    if ((data.dateOfBirth ?? '') !== (user.dateOfBirth ?? '')) {
      updateData.dateOfBirth = data.dateOfBirth;
    }

    await onSubmit(updateData);
  };

  // Check if user has external accounts
  const hasPunchhAccount = !!user.punchId;
  const hasOloAccount = !!user.oloId;
  const hasExternalAccount = hasPunchhAccount || hasOloAccount;

  return (
    <div className="space-y-4">
      {/* Warning for users with external accounts */}
      {hasExternalAccount && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Phone changes will sync to{' '}
            {[hasPunchhAccount && 'Punchh', hasOloAccount && 'Olo']
              .filter(Boolean)
              .join(' and ')}{' '}
            where linked. Other changes sync atomically to Punchh.
          </AlertDescription>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleFormSubmit(handleSubmit)} className="space-y-4">
        {/* First Name */}
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            {...register('firstName')}
            placeholder="John"
            aria-invalid={errors.firstName ? 'true' : 'false'}
          />
          {errors.firstName && (
            <p className="text-sm text-destructive">{errors.firstName.message}</p>
          )}
        </div>

        {/* Last Name */}
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            {...register('lastName')}
            placeholder="Doe"
            aria-invalid={errors.lastName ? 'true' : 'false'}
          />
          {errors.lastName && <p className="text-sm text-destructive">{errors.lastName.message}</p>}
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            placeholder="john.doe@example.com"
            aria-invalid={errors.email ? 'true' : 'false'}
          />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            {...register('phone')}
            placeholder="+12345678901"
            aria-invalid={errors.phone ? 'true' : 'false'}
          />
          {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
        </div>

        {/* Date of Birth */}
        <div className="space-y-2">
          <Label htmlFor="dateOfBirth">Date of Birth (Optional)</Label>
          <Input
            id="dateOfBirth"
            type="date"
            {...register('dateOfBirth')}
            aria-invalid={errors.dateOfBirth ? 'true' : 'false'}
          />
          {errors.dateOfBirth && (
            <p className="text-sm text-destructive">{errors.dateOfBirth.message}</p>
          )}
        </div>

        {/* Push Notifications Enabled */}
        <div className="flex items-center gap-2">
          <input
            id="pushNotificationsEnabled"
            type="checkbox"
            {...register('pushNotificationsEnabled')}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="pushNotificationsEnabled" className="cursor-pointer">
            Push Notifications Enabled
          </Label>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}
